import type { ChatTransport, UIMessageChunk } from "ai";
import { lumeBearer } from "./agent-session";
import { isSeedMessage } from "./invite-seed";
import type { LumeAgentRequest } from "./request-context";
import type { LumeUIMessage } from "./types";

/**
 * Talks to the Lume agent (the Mastra service in lume_ai/mastra-service).
 *
 * It exists because the two ends speak different dialects of the same idea. The
 * drawer is an AI SDK `useChat`, which expects a UI message stream —
 * `text-start` / `text-delta` / `text-end`, `tool-input-available`,
 * `tool-output-available`. Mastra streams its own envelope instead:
 *
 *   data: {"type":"text-delta","runId":"…","from":"AGENT","payload":{"id":"…","text":"Não"}}
 *
 * Same events, wrapped in `payload` and named slightly differently, and Mastra's
 * HTTP route has no flag to emit the AI SDK shape (checked: `format: "aisdk"` on
 * the body is ignored). So this translates, and `DefaultChatTransport` stays
 * unusable for us. The mapping below was written against a captured stream rather
 * than from documentation.
 *
 * What it sends, in three separate places for three different reasons:
 *
 *   Authorization header  the student's session, as one bearer token. The service
 *                         protects every /api route with it, and a header is the
 *                         only place that can work for routes with no body.
 *   memory.thread         which conversation this is — one per caderno, so the
 *                         transcript outlives the tab. The resource (the student)
 *                         is NOT sent: the service derives it from the verified
 *                         session and prefers that over anything from here.
 *   requestContext        page context only. What is on the student's screen, which
 *                         changes with every navigation and is not a credential.
 *
 * Only the newest message goes up: the thread is the history now.
 */

/**
 * The agent refused the turn because the student is out of messages for today.
 *
 * A TYPE AND NOT A MESSAGE, so the drawer can answer it with the TM PLUS offer
 * instead of the "something went wrong" notice every other failure gets. This is
 * not a failure at all — the service worked exactly as intended — and rendering
 * it as one told the student to try again, which would never have helped.
 *
 * THE WIRE CONTRACT. The service refuses through a Mastra processor tripwire
 * (lume/chat-quota.ts), which arrives as one chunk in the ordinary stream — the
 * same envelope as every other chunk, so nothing special happens at the HTTP
 * layer and the response is a 200:
 *
 *   { "type": "tripwire", "from": "AGENT",
 *     "payload": { "reason": "…",
 *                  "metadata": { "code": "chat_daily_limit",
 *                                "limit": 3, "used": 3 } } }
 *
 * ONLY that code raises this. A tripwire is the framework's general "this turn
 * stops here", and another processor could raise one for an unrelated reason —
 * treating every tripwire as a paywall would sell a subscription to someone whose
 * message was refused for a completely different reason.
 */
export class ChatLimitError extends Error {
  /** Messages a free account gets per day, when the service said. */
  readonly limit: number | null;

  constructor(limit: number | null) {
    super("Suas mensagens de hoje acabaram.");
    this.name = "ChatLimitError";
    this.limit = limit;
  }
}

/** Mastra's SSE envelope. `payload` carries the parts that differ per type. */
interface MastraChunk {
  type?: string;
  payload?: {
    id?: string;
    text?: string;
    toolCallId?: string;
    toolName?: string;
    args?: unknown;
    result?: unknown;
    error?: { message?: string } | string;
    /** Only on a `tripwire` chunk — see ChatLimitError. */
    metadata?: unknown;
  };
}

export class MastraChatTransport implements ChatTransport<LumeUIMessage> {
  private readonly endpoint: string | null;
  private readonly threadId: string | null;
  private readonly sentSeeds = new Set<string>();

  /**
   * @param baseUrl the service root, e.g. http://localhost:4111. Null when no agent
   *   is configured, which is an error at send time rather than a silent fallback.
   * @param agentId the agent Mastra registers, e.g. "caderno"
   * @param threadId the conversation this transport writes to, or null when the
   *   service could not give us one. PASSED IN, not derived here: the service
   *   generates thread ids and the same one is used to read the history back
   *   (agent/agent-session.ts), so deriving it twice is how the drawer ends up
   *   displaying one conversation while writing to another.
   */
  constructor(baseUrl: string | null, agentId: string, threadId: string | null) {
    this.endpoint =
      baseUrl === null || baseUrl === ""
        ? null
        : `${baseUrl.replace(/\/$/, "")}/api/agents/${agentId}/stream`;
    this.threadId = threadId;
  }

  async sendMessages(
    options: Parameters<ChatTransport<LumeUIMessage>["sendMessages"]>[0]
  ): Promise<ReadableStream<UIMessageChunk>> {
    if (this.endpoint === null) {
      // A configuration problem, not a student one — so it is logged for whoever
      // deployed it and worded plainly for whoever is reading the drawer
      console.error(
        "[lume] No agent URL configured (LUME_AGENT_URL in config.php, emitted as window.__LUME_AGENT_URL__)"
      );

      throw new Error("O Lume não está disponível neste momento. Já estamos verificando.");
    }

    const request = options.body as LumeAgentRequest | undefined;

    if (this.threadId === null) {
      // Not a degraded mode — an impossible one. These agents have memory
      // configured, and Mastra refuses a turn with no thread ("A resourceId and a
      // threadId must be provided when using Memory. Saw threadId 'undefined'"), so
      // sending anyway would spend the round trip to earn that error. chat-session
      // always supplies one (agent-session.ts falls back to a derived id rather than
      // returning nothing), so this is a guard against a future regression, not a
      // path the student reaches.
      console.error("[lume] No conversation thread for this agent surface");

      throw new Error("Esta conversa não abriu como devia. Recarregue a página para começar de novo.");
    }

    const bearer = lumeBearer();

    if (bearer === null) {
      // Without credentials every route on that service answers 401, so there is
      // nothing to gain by sending the turn and paying the round trip
      console.error("[lume] No student session on the request; refusing to call the agent");

      throw new Error(
        "Sua sessão expirou. Recarregue a página para continuar falando com o Lume."
      );
    }

    const seeds = this.unsentSeeds(options.messages);
    let response: Response;

    try {
      response = await fetch(this.endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // The session, as the one credential Mastra's auth hook is handed. It used
          // to ride in the body on requestContext, which could not protect a route
          // without one (a GET has no body) — see the service's lume/auth-provider.ts.
          Authorization: `Bearer ${bearer}`,
        },
        signal: options.abortSignal,
        body: JSON.stringify({
          // ONLY THE NEW TURN. The thread is the transcript, so sending the whole
          // array would append every earlier message to it again on each turn — a
          // thread growing quadratically and a bill to match.
          messages: toMastraMessages([...seeds, ...options.messages.slice(-1)]),
          // `resource` is deliberately absent: the service derives it from the
          // verified session (mapUserToResourceId) and ignores anything sent from
          // here, which is what stops one student addressing another's memory.
          memory: { thread: this.threadId },
          requestContext: {
            // No credentials here any more — only what the agent needs to know about
            // the screen, which changes with every navigation
            lumePageContext: request?.context,
          },
        }),
      });
    } catch (cause) {
      // The student pressing "parar" aborts this very fetch, and a stop is not a
      // failure — it is re-thrown untouched so the SDK treats it as one.
      if (cause instanceof Error && cause.name === "AbortError") {
        throw cause;
      }

      // Everything else is a request that never arrived: the service is down, the
      // connection dropped, DNS. fetch rejects with a bare TypeError whose message
      // ("Failed to fetch", "Load failed" — it differs per browser) is English,
      // technical, and was never written to be read by anyone; the drawer used to
      // show it verbatim.
      console.error("[lume] Agent request never left", cause);

      throw new Error(
        "A conexão caiu no meio do caminho. Verifique sua internet e tente de novo."
      );
    }

    if (!response.ok || response.body === null) {
      // Logged with the status, because 401 (the student's session expired) and 500
      // (the agent broke) need different fixes and the drawer cannot say which
      console.error(
        `[lume] Agent request failed: ${response.status} ${response.statusText}`
      );

      // The drawer titles the notice itself (ChatNotice/chatFailure) and shows
      // this as the line under it, so it says what happened and nothing else
      throw new Error(
        response.status === 401
          ? "Sua sessão expirou. Recarregue a página para continuar falando com o Lume."
          : "O serviço do Lume não respondeu como esperávamos. Tente de novo em alguns segundos."
      );
    }

    for (const seed of seeds) {
      this.sentSeeds.add(seed.id);
    }

    return toUiMessageStream(response.body);
  }

  /**
   * Lume's side of an invite opening (agent/invite-seed.ts), sent once, ahead of
   * the student's first message: the thread has never seen it, and without it the
   * agent would be answering a reply to a proposal it never made. The seeded
   * student message stays behind so that only the message they typed counts.
   */
  private unsentSeeds(messages: LumeUIMessage[]): LumeUIMessage[] {
    return messages
      .slice(0, -1)
      .filter(
        message =>
          isSeedMessage(message) &&
          message.role === "assistant" &&
          !this.sentSeeds.has(message.id)
      );
  }

  /**
   * No resumable stream on the Mastra side, so a dropped connection is a dropped
   * turn — same as the stub transport this replaces.
   */
  async reconnectToStream(): Promise<ReadableStream<UIMessageChunk> | null> {
    return null;
  }
}

/** One part of a message as the model sees it: words, or a picture. */
type MastraContentPart =
  | { type: "text"; text: string }
  | { type: "image"; image: string; mimeType?: string };

/**
 * UI messages down to what Mastra's route accepts: a role, and either a string or
 * a list of parts.
 *
 * Tool parts are dropped deliberately. The drawer renders text only
 * (ChatBubble.tsx), so they would be invisible history — and the agent re-reads
 * whatever it needs through the study tools, which are cheap and always current.
 * Sending a stale tool result back would be worse than not sending it: it invites
 * the model to quote yesterday's percentage.
 *
 * FILE PARTS ARE NOT DROPPED, and are the one exception. A photo IS the question —
 * "me ajuda com essa" means nothing without the picture of the exercise — so it
 * travels as an `image` part beside the text.
 *
 * THE IMAGE IS INLINE, as the data: URL the service handed back
 * (agent/photo-upload.ts), and not a link. A link would have to be fetchable by
 * the model provider, which rules out both places one could live: a developer's
 * `todamateria.test` is not reachable from the internet, and a path on the public
 * site would leave a minor's homework behind nothing but an unguessable name.
 *
 * A message keeps the plain STRING form when it is text alone. Not tidiness — the
 * string form is the shape every turn has always taken, and a one-element parts
 * array is a different thing for the service's message list to store and read
 * back. Only a message that actually carries a picture changes shape.
 *
 * Called with the LAST message only: the thread on the service side holds the
 * transcript now, so anything already sent is already there.
 */
function toMastraMessages(
  messages: LumeUIMessage[]
): Array<{ role: string; content: string | MastraContentPart[] }> {
  const converted: Array<{ role: string; content: string | MastraContentPart[] }> = [];

  for (const message of messages) {
    const text = message.parts
      .filter(part => part.type === "text")
      .map(part => (part as { text: string }).text)
      .join("");

    const images = message.parts
      .filter(
        part =>
          part.type === "file" &&
          typeof (part as { mediaType?: unknown }).mediaType === "string" &&
          (part as { mediaType: string }).mediaType.startsWith("image/")
      )
      .map(part => part as { mediaType: string; url: string })
      .filter(part => typeof part.url === "string" && part.url !== "");

    if (images.length === 0) {
      if (text.trim() !== "") {
        converted.push({ role: message.role, content: text });
      }

      continue;
    }

    const parts: MastraContentPart[] = images.map(image => ({
      type: "image",
      image: image.url,
      mimeType: image.mediaType,
    }));

    // THE PICTURE FIRST, THEN THE QUESTION. A model reads the parts in order, and
    // "o que está errado aqui?" only means something once the thing it points at
    // has been seen — asking before showing invites a guess that the image then
    // has to correct.
    //
    // And a photo sent with no words gets a sentence anyway: an image on its own
    // leaves the model to infer what was wanted, and the inference is usually
    // "describe this". Naming the intent — help me with what is in here — is what
    // makes a bare photo behave like the question the student meant it as.
    parts.push({
      type: "text",
      text:
        text.trim() !== ""
          ? text
          : "Mandei essa foto. Me ajuda com o que está nela.",
    });

    converted.push({ role: message.role, content: parts });
  }

  return converted;
}

/**
 * Mastra's SSE → the AI SDK's UI message chunks.
 *
 * Reasoning chunks are dropped on purpose: they carry the provider's encrypted
 * reasoning blobs, the drawer has nowhere to put them, and a tutor's private
 * working is not something to stream at a student.
 */
function toUiMessageStream(
  body: ReadableStream<Uint8Array>
): ReadableStream<UIMessageChunk> {
  const decoder = new TextDecoder();
  let buffer = "";

  return new ReadableStream<UIMessageChunk>({
    async start(controller) {
      const reader = body.getReader();

      try {
        for (;;) {
          const { done, value } = await reader.read();

          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });

          // SSE events are separated by a blank line; a partial one stays in the
          // buffer until the rest of it arrives
          const events = buffer.split("\n\n");
          buffer = events.pop() ?? "";

          for (const event of events) {
            const chunk = parseEvent(event);

            if (chunk === "done") {
              controller.close();

              return;
            }

            if (chunk !== null) {
              // The quota refusal, and not a stream error: raised as the typed
              // error so the drawer answers it with the TM PLUS offer instead of
              // the "algo deu errado" notice. Errors the controller, which is
              // what surfaces it on useChat's `error`.
              const quota = chatLimitOf(chunk);

              if (quota !== null) {
                controller.error(new ChatLimitError(quota.limit));

                return;
              }

              for (const translated of translate(chunk)) {
                controller.enqueue(translated);
              }
            }
          }
        }

        controller.close();
      } catch (cause) {
        controller.error(cause);
      } finally {
        reader.releaseLock();
      }
    },
  });
}

function parseEvent(event: string): MastraChunk | "done" | null {
  for (const line of event.split("\n")) {
    if (!line.startsWith("data:")) {
      continue;
    }

    const data = line.slice(5).trim();

    if (data === "[DONE]") {
      return "done";
    }

    try {
      return JSON.parse(data) as MastraChunk;
    } catch {
      // A frame we cannot read is skipped rather than fatal: losing one delta
      // beats collapsing the whole reply
      return null;
    }
  }

  return null;
}

/** One Mastra chunk to zero or more UI chunks. */
function translate(chunk: MastraChunk): UIMessageChunk[] {
  const payload = chunk.payload ?? {};

  switch (chunk.type) {
    case "start":
      return [{ type: "start" }];

    case "step-start":
      return [{ type: "start-step" }];

    case "text-start":
      return payload.id === undefined
        ? []
        : [{ type: "text-start", id: payload.id }];

    case "text-delta":
      return payload.id === undefined || payload.text === undefined
        ? []
        : [{ type: "text-delta", id: payload.id, delta: payload.text }];

    case "text-end":
      return payload.id === undefined
        ? []
        : [{ type: "text-end", id: payload.id }];

    // Mastra announces a tool call before its arguments have finished streaming.
    // Surfaced so the drawer can show that something is happening during the
    // second or two a study tool takes.
    case "tool-call-input-streaming-start":
      return payload.toolCallId === undefined || payload.toolName === undefined
        ? []
        : [
            {
              type: "tool-input-start",
              toolCallId: payload.toolCallId,
              toolName: payload.toolName,
            },
          ];

    case "tool-call":
      return payload.toolCallId === undefined || payload.toolName === undefined
        ? []
        : [
            {
              type: "tool-input-available",
              toolCallId: payload.toolCallId,
              toolName: payload.toolName,
              input: payload.args,
            },
          ];

    case "tool-result":
      return payload.toolCallId === undefined
        ? []
        : [
            {
              type: "tool-output-available",
              toolCallId: payload.toolCallId,
              output: payload.result,
            },
          ];

    case "step-finish":
      return [{ type: "finish-step" }];

    case "finish":
      return [{ type: "finish" }];

    case "error":
      return [{ type: "error", errorText: errorTextOf(payload.error) }];

    default:
      return [];
  }
}

function errorTextOf(error: MastraChunk["payload"] extends undefined ? never : unknown): string {
  if (typeof error === "string" && error !== "") {
    return error;
  }

  if (error !== null && typeof error === "object") {
    const message = (error as { message?: unknown }).message;

    if (typeof message === "string" && message !== "") {
      return message;
    }
  }

  return "O Lume não conseguiu responder agora.";
}

/**
 * The quota refusal off a chunk, or null when this chunk is not one.
 *
 * Two questions, and they need telling apart: "is this the quota tripwire" and
 * "did it say what the limit is". So a match is an OBJECT — always truthy, even
 * when the number inside it is missing — and a non-match is null. A bare
 * `number | null` would have collapsed "not a paywall" into "a paywall with no
 * number", which is the difference between showing the offer and not.
 *
 * The CODE identifies it; the number is a bonus the copy does without.
 */
function chatLimitOf(chunk: MastraChunk): { limit: number | null } | null {
  if (chunk.type !== "tripwire") {
    return null;
  }

  const metadata = chunk.payload?.metadata;

  if (
    metadata === null ||
    typeof metadata !== "object" ||
    (metadata as { code?: unknown }).code !== "chat_daily_limit"
  ) {
    return null;
  }

  const limit = (metadata as { limit?: unknown }).limit;

  return {
    limit: typeof limit === "number" && Number.isFinite(limit) ? limit : null,
  };
}
